import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TrendingUp, TrendingDown, ArrowRight, GitCompare, Trophy, AlertTriangle } from "lucide-react";
import { fmtNum, MONTH_NAMES } from "@/lib/helpers";

function DeltaBadge({ value, pct }) {
  if (value === 0) return <Badge className="bg-slate-700 text-slate-400 border-slate-600">—</Badge>;
  const up = value > 0;
  return (
    <Badge className={up ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/40" : "bg-red-500/20 text-red-300 border-red-500/40"}>
      {up ? <TrendingUp size={10} className="mr-1" /> : <TrendingDown size={10} className="mr-1" />}
      {up ? "+" : ""}{fmtNum(value)} ({pct > 0 ? "+" : ""}{pct}%)
    </Badge>
  );
}

function StatCompare({ title, cur, prev, pct, hint }) {
  const up = cur - prev >= 0;
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900/60 glass p-5">
      <div className="text-[10px] uppercase font-mono text-slate-500">{title}</div>
      <div className="flex items-end gap-3 mt-2">
        <div className="font-display text-3xl font-bold">{fmtNum(cur)}</div>
        <div className="text-xs text-slate-500 pb-1">önceki: {fmtNum(prev)}</div>
      </div>
      <div className="mt-2">
        <Badge className={up ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/40" : "bg-red-500/20 text-red-300 border-red-500/40"}>
          {up ? <TrendingUp size={10} className="mr-1" /> : <TrendingDown size={10} className="mr-1" />}
          {pct > 0 ? "+" : ""}{pct}%
        </Badge>
      </div>
      {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
    </div>
  );
}

function TopList({ title, items, kind = "gain" }) {
  const Icon = kind === "gain" ? Trophy : AlertTriangle;
  return (
    <Card className="border-slate-800 bg-slate-900/60 glass">
      <CardHeader>
        <CardTitle className="text-sm flex items-center gap-2">
          <Icon size={14} className={kind === "gain" ? "text-amber-400" : "text-red-400"} /> {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-1">
        {items.length === 0 ? <div className="text-xs text-slate-500 italic">Yok</div> :
          items.map((e, i) => (
            <div key={e.key} className="flex items-center gap-2 text-sm p-2 rounded-lg bg-slate-800/40 border border-slate-800">
              <span className="text-[10px] text-slate-500 font-mono w-5">#{i+1}</span>
              <div className="flex-1 min-w-0">
                <div className="truncate font-medium">{e.label}</div>
                <div className="text-[10px] text-slate-500">{fmtNum(e.previous_points)} → {fmtNum(e.current_points)}</div>
              </div>
              <DeltaBadge value={e.diff} pct={e.pct_change} />
            </div>
          ))
        }
      </CardContent>
    </Card>
  );
}

export default function Comparison() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);

  useEffect(() => {
    http.get(`/reports/comparison?year=${year}&month=${month}`).then((r) => setData(r.data));
  }, [year, month]);

  if (!data) return <div className="text-slate-400">Yükleniyor...</div>;

  return (
    <div className="space-y-6">
      <header className="animate-fade-up flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold flex items-center gap-2">
            <GitCompare className="text-blue-400" /> Aylık Karşılaştırma
          </h1>
          <p className="text-slate-400 text-sm mt-1 flex items-center gap-2 flex-wrap">
            <span>{MONTH_NAMES[data.previous.month - 1]} {data.previous.year}</span>
            <ArrowRight size={12} />
            <span className="font-semibold">{MONTH_NAMES[data.current.month - 1]} {data.current.year}</span>
          </p>
        </div>
        <div className="flex gap-2">
          <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
            <SelectTrigger className="bg-slate-900/60 border-slate-700 w-32" data-testid="cmp-month"><SelectValue /></SelectTrigger>
            <SelectContent>{MONTH_NAMES.map((m, i) => <SelectItem key={i} value={String(i+1)}>{m}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="bg-slate-900/60 border-slate-700 w-28"><SelectValue /></SelectTrigger>
            <SelectContent>{[2025, 2026, 2027].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </header>

      <div className="grid gap-4 sm:grid-cols-3 animate-fade-up delay-100">
        <StatCompare title="İşlem Adedi" cur={data.current.totals.count} prev={data.previous.totals.count} pct={data.deltas.count_pct} />
        <StatCompare title="Satış Adedi" cur={data.current.totals.qty} prev={data.previous.totals.qty} pct={data.deltas.qty_pct} />
        <StatCompare title="Toplam Puan" cur={data.current.totals.points} prev={data.previous.totals.points} pct={data.deltas.points_pct} />
      </div>

      <div className="grid gap-4 lg:grid-cols-2 animate-fade-up delay-200">
        <TopList title="En Çok Artan Şubeler" items={data.top_gainers_branch} kind="gain" />
        <TopList title="En Çok Düşen Şubeler" items={data.top_losers_branch} kind="loss" />
        <TopList title="En Çok Artan Ürünler" items={data.top_gainers_product} kind="gain" />
        <TopList title="En Çok Düşen Ürünler" items={data.top_losers_product} kind="loss" />
        <TopList title="En Çok Artan Personel" items={data.top_gainers_employee} kind="gain" />
        <TopList title="En Çok Düşen Personel" items={data.top_losers_employee} kind="loss" />
      </div>

      <Card className="border-slate-800 bg-slate-900/60 glass animate-fade-up delay-300">
        <CardHeader>
          <CardTitle className="text-base">Tüm Şubeler</CardTitle>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-slate-800 hover:bg-transparent">
                <TableHead>Şube</TableHead>
                <TableHead className="text-right">Önceki Puan</TableHead>
                <TableHead className="text-right">Bu Ay</TableHead>
                <TableHead className="text-right">Fark</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.branches.map((e) => (
                <TableRow key={e.key} className="border-slate-800">
                  <TableCell>
                    <div className="font-semibold">{e.label}</div>
                    <div className="text-[10px] font-mono text-slate-500">{e.branch_code}</div>
                  </TableCell>
                  <TableCell className="text-right text-slate-400">{fmtNum(e.previous_points)}</TableCell>
                  <TableCell className="text-right font-bold text-amber-300">{fmtNum(e.current_points)}</TableCell>
                  <TableCell className="text-right"><DeltaBadge value={e.diff} pct={e.pct_change} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card className="border-slate-800 bg-slate-900/60 glass animate-fade-up delay-300">
        <CardHeader>
          <CardTitle className="text-base">Tüm Ürünler</CardTitle>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-slate-800 hover:bg-transparent">
                <TableHead>Ürün</TableHead>
                <TableHead>SKU</TableHead>
                <TableHead className="text-right">Önceki</TableHead>
                <TableHead className="text-right">Bu Ay</TableHead>
                <TableHead className="text-right">Fark</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.products.map((e) => (
                <TableRow key={e.key} className="border-slate-800">
                  <TableCell className="font-semibold">{e.label}</TableCell>
                  <TableCell className="font-mono text-xs text-slate-500">{e.sku}</TableCell>
                  <TableCell className="text-right text-slate-400">{fmtNum(e.previous_points)}</TableCell>
                  <TableCell className="text-right font-bold text-amber-300">{fmtNum(e.current_points)}</TableCell>
                  <TableCell className="text-right"><DeltaBadge value={e.diff} pct={e.pct_change} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
